<template>
	<common-card :class="cardClasses" @click="handleCardAction">
		<img v-if="image" class="omapi-integration-card__image" :src="image">
		<div v-if="provider.name" class="omapi-integration-card__name">
			{{ provider.name }}
		</div>
		<div v-if="connections" class="omapi-integration-card__connections">
			{{ connections }}
		</div>
		<div class="omapi-integration-card__cta">
			<span v-html="actionName"></span>
		</div>
	</common-card>
</template>
<script>
import { strings } from '@/campaigns/mixins/strings'
import { integration } from '@/integrations/mixins/integration'
import { mapState } from 'vuex'

export default {
	mixins : [ integration, strings ],
	props  : {
		/**
		 * Provider
		 */
		provider : {
			type : Object,
		},
	},
	computed : {
		...mapState('settings', [
			'analyticsAccounts',
		]),
		...mapState('integrations', [
			'activeCard',
			'activeContent',
			'addingIntegration',
			'filters',
			'search',
		]),
		/**
		 * Connections
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The number of connections string.
		 */
		connections () {
			// OM & Google Analytics get custom responses.
			if (this.isOmAnalytics) {
				return '(pre-configured)'
			}

			const count = this.isGoogleAnalytics
				? this.analyticsAccounts.length
				: this.provider.integrations.length

			if (!count) {
				return this.isGoogleAnalytics ? 'No Accounts' : false
			}

			return `${count} ${this.makePlural('Connection', count)}`
		},
		/**
		 * Image
		 *
		 * @since 2.4.0
		 *
		 * @returns {Object} The provider image/logo.
		 */
		image () {
			let image
			const slug   = this.provider.slug.replace(/-(featured|existing)/, '')
			const images = require.context('../../assets/images/providers/', false, /\.png$/)

			try {
				image = this.provider.slug ? images(`./${slug}.png`) : ''
			} catch (e) {
				image = images('./fallbackprovider.png')
			}

			return image
		},
		/**
		 * Card Classes
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The CSS classes for this card.
		 */
		cardClasses () {
			return `omapi-integration-card${this.isActive ? ' active' : ''}`
		},
		/**
		 * Card State
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The card state.
		 */
		cardState () {
			if (this.isActive && this.addingIntegration) {
				return 'cancelable'
			}

			if (this.isActive && !this.addingIntegration) {
				return 'closeable'
			}

			if ((this.forceConfigurable || this.hasIntegrations) && !this.isActive) {
				return 'configurable'
			}

			return 'connectable'
		},
		/**
		 * Action Name
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The action name.
		 */
		actionName () {
			if ('fallbackprovider' === this.provider.slug) {
				return 'Explore Options &rarr;'
			}
			switch (this.cardState) {
			case 'cancelable':
				return 'Cancel'
			case 'closeable':
				return '&larr; Close'
			case 'configurable':
				return 'Configure &rarr;'
			default:
				return 'Connect &rarr;'
			}
		},
	},
	methods : {
		/**
		 * Handle Card Action
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		handleCardAction () {
			if (!this.$store.getters.connected) {
				return this.$modal.show('not-connected')
			}

			switch (this.cardState) {
			case 'closeable':
			case 'cancelable':
				this.handleClose()
				break
			case 'configurable':
				this.handleConfigure()
				break
			default:
				this.handleConnect()
			}
		},
	},
}
</script>
